/**
 * Supplier Performance Ranking — Phase P3-9.
 *
 * Global leaderboard of suppliers ordered by composite score, with
 * optional category filter. Click a row to drill down into the
 * scorecard.
 *
 * Route: /admin/procurement/suppliers/ranking
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Search, ChevronRight, Award } from 'lucide-react';

import { Card, CardContent } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import BackButton from '../../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRADE_TINTS = {
  A: 'bg-emerald-100 text-emerald-800',
  B: 'bg-green-100 text-green-800',
  C: 'bg-amber-100 text-amber-800',
  D: 'bg-red-100 text-red-800',
  E: 'bg-red-200 text-red-900',
};

const RISK_TINTS = {
  low: 'text-green-700',
  medium: 'text-amber-700',
  high: 'text-red-700',
};

const formatSpend = (spend) => {
  const entries = Object.entries(spend || {});
  if (!entries.length) return '—';
  const [primary] = entries.sort((a, b) => b[1] - a[1]);
  const [ccy, val] = primary;
  return `${ccy} ${Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const SupplierRanking = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await axios.get(`${API_URL}/api/procurement/suppliers/score/ranking?limit=50`, auth);
        if (!cancelled) setRows(r.data || []);
      } catch (e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const categories = useMemo(() => {
    const set = new Set(rows.map((r) => r.category).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (category !== 'all' && r.category !== category) return false;
      if (q && !(r.supplier_name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, category]);

  return (
    <div className="p-6 space-y-4" data-testid="supplier-ranking-page">
      <BackButton />

      <Card>
        <CardContent className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md" style={{ backgroundColor: '#023E8A15', color: '#023E8A' }}>
                <Trophy className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Supplier Performance Ranking</h1>
                <p className="text-xs text-slate-500">
                  Composite score based on delivery, on-time, rejection, lead time, spend and invoice reliability.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search supplier…"
                  className="pl-8 h-9 w-[240px]"
                  data-testid="ranking-search"
                />
              </div>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-9 w-[200px]" data-testid="ranking-category">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 w-10">#</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Supplier</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Category</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">Score</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600">Grade</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">Accuracy</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">On-Time</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">Lead time</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">Spend</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600">Risk</th>
                  <th className="px-3 py-2 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={11} className="py-8 text-center text-slate-500">Loading ranking…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={11} className="py-8 text-center text-slate-400">No suppliers match the filter</td></tr>
                )}
                {filtered.map((r, i) => (
                  <tr
                    key={r.supplier_id}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/procurement/suppliers/${r.supplier_id}/score`)}
                    data-testid={`ranking-row-${r.supplier_id}`}
                  >
                    <td className="px-3 py-2 font-semibold text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-slate-800 truncate">{r.supplier_name}</td>
                    <td className="px-3 py-2 text-slate-600">{r.category}</td>
                    <td className="px-3 py-2 text-right font-bold" style={{ color: '#023E8A' }}>
                      {r.composite_score.toFixed(1)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${GRADE_TINTS[r.grade] || GRADE_TINTS.E}`}>
                        <Award className="h-3 w-3" />
                        {r.grade}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{r.delivery_accuracy_pct}%</td>
                    <td className="px-3 py-2 text-right">{r.on_time_delivery_pct}%</td>
                    <td className="px-3 py-2 text-right">
                      {r.avg_lead_time_days > 0 ? '+' : ''}{r.avg_lead_time_days}d
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">{formatSpend(r.total_spend)}</td>
                    <td className={`px-3 py-2 text-center text-xs font-semibold uppercase ${RISK_TINTS[r.risk_level] || 'text-slate-500'}`}>
                      {r.risk_level}
                    </td>
                    <td className="px-3 py-2">
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplierRanking;
